import React, { useState } from 'react';
import {
    Button,
    Calendar,
    Layout,
    Menu,
    Typography,
    List,
    Badge,
    Space,
    Radio,
    Card,
    theme,
} from 'antd';
import {
    LeftOutlined,
    RightOutlined,
    PlusOutlined,
    SettingOutlined,
    CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title } = Typography;
const { useToken } = theme;

const Scheduler = () => {
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [viewMode, setViewMode] = useState('month');
    const { token } = useToken();

    // Sample event data
    const events = [
        {
            id: '8506',
            title: 'Edited Event 8506',
            date: dayjs('2025-01-04'),
            time: '05:30 - 05:30',
            type: 'all-day',
        },
    ];

    const categories = [
        { key: 'meeting', label: 'Meeting', color: token.blue6 },
        { key: 'holiday', label: 'Holiday', color: token.green6 },
        { key: 'conference', label: 'Conference', color: token.purple6 },
        { key: 'birthday', label: 'Birthday', color: token.orange6 },
    ];

    // Header renderer for calendar navigation
    const headerRender = ({ value, onChange }) => {
        const month = value.format('MMMM YYYY');

        return (
            <div
                style={{
                    padding: '16px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${token.colorBorderSecondary}`,
                }}
            >
                <Space size="middle">
                    <Space>
                        <Button
                            icon={<LeftOutlined />}
                            onClick={() => onChange(value.subtract(1, 'month'))}
                            type="text"
                            size="small"
                        />
                        <Button
                            icon={<RightOutlined />}
                            onClick={() => onChange(value.add(1, 'month'))}
                            type="text"
                            size="small"
                        />
                    </Space>
                    <Title level={4} style={{ margin: 0 }}>
                        {month}
                    </Title>
                </Space>
                <Radio.Group
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                >
                    <Radio.Button value="month">Month</Radio.Button>
                    <Radio.Button value="year">Year</Radio.Button>
                </Radio.Group>
            </div>
        );
    };

    // Render events in calendar date cells
    const dateCellRender = (value) => {
        const dayEvents = events.filter((event) =>
            event.date.isSame(value, 'day')
        );

        return dayEvents.map((event) => (
            <Badge
                key={event.id}
                color={token.colorPrimary}
                text={event.title}
                style={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '12px',
                    padding: '2px 0',
                }}
            />
        ));
    };

    return (
        <Layout style={{ minHeight: '100vh', backgroundColor:'transparent'}}>
            <div
                style={{
                    display: 'flex',
                    gap: '24px',
                    maxWidth: '1600px',
                    margin: '0 auto',
                }}
            >
                {/* Sidebar */}
                <div style={{ width: '320px' }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        block
                        size="large"
                        style={{
                            marginBottom: 24,
                            height: '48px',
                            fontSize: '16px',
                            boxShadow: token.boxShadow,
                        }}
                    >
                        Create event
                    </Button>

                    {/* Upcoming Events */}
                    <Card
                        title={
                            <Space>
                                <CalendarOutlined style={{ color: token.colorPrimary }} />
                                <span style={{ fontWeight: 600 }}>Upcoming events</span>
                            </Space>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: token.borderRadiusLG,
                            boxShadow: token.boxShadowTertiary,
                            body: {
                                padding: '0 24px',
                            },
                        }}
                    >
                        <List
                            size="small"
                            dataSource={events}
                            renderItem={(event) => (
                                <List.Item
                                    style={{
                                        padding: '16px 0',
                                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                    }}
                                >
                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                        <div
                                            style={{
                                                color: token.colorTextSecondary,
                                                fontSize: '13px',
                                            }}
                                        >
                                            Today, {event.time}
                                        </div>
                                        <div
                                            style={{
                                                fontWeight: 500,
                                                color: token.colorText,
                                                fontSize: '14px',
                                            }}
                                        >
                                            {event.title}
                                        </div>
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </Card>

                    {/* Event Categories */}
                    <Card
                        title={
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <span style={{ fontWeight: 600 }}>Categories</span>
                                <Button
                                    type="text"
                                    icon={
                                        <SettingOutlined
                                            style={{ color: token.colorTextSecondary }}
                                        />
                                    }
                                    style={{ border: 'none', padding: 0 }}
                                />
                            </div>
                        }
                        style={{
                            borderRadius: token.borderRadiusLG,
                            boxShadow: token.boxShadowTertiary,
                            body: {
                                padding: 0
                            },
                        }}
                    >
                        <Menu
                            mode="inline"
                            items={categories.map((category) => ({
                                key: category.key,
                                label: (
                                    <Space>
                                        <Badge color={category.color} />
                                        <span>{category.label}</span>
                                    </Space>
                                ),
                            }))}
                            style={{
                                border: 'none',
                                backgroundColor: 'transparent',
                            }}
                        />
                    </Card>
                </div>

                {/* Calendar Content */}
                <Content>
                    <Card
                        style={{
                            borderRadius: token.borderRadiusLG,
                            boxShadow: token.boxShadowTertiary,
                            body: {
                                padding: 0
                            },
                        }}
                    >
                        <Calendar
                            value={selectedDate}
                            onChange={setSelectedDate}
                            headerRender={headerRender}
                            cellRender={dateCellRender}
                            mode={viewMode}
                            style={{
                                backgroundColor: token.colorBgContainer,
                                borderRadius: token.borderRadiusLG,
                            }}
                        />
                    </Card>
                </Content>
            </div>
        </Layout>
    );
};

export default Scheduler;
